import { DestroyRef, Directive, inject, OnDestroy } from "@angular/core";

import { Subject } from "rxjs";

import { IAddressModel, KeyValuePairModel } from "./models/common.models";
import { memoize } from "lodash";

/**
 * This is a class that should be used to extend every component class.
 * This class will cleanup any observables when the component is destoryed.
 * This is to prevent memory leaks.
 *
 * It will also implement any methods that should be used on most/all components.
 */
@Directive()
export class ComponentBase implements OnDestroy {
  destroyRef = inject(DestroyRef);
  ngUnsubscribe$ = new Subject();

  constructor() {}

  ngOnDestroy() {
    this.unsubscribe();
  }

  unsubscribe() {
    this.ngUnsubscribe$.next(false);
    this.ngUnsubscribe$.unsubscribe();
  }

  // Instead of calculating in the template
  memoizedGetValueOrDash = memoize((value: any) => {
    return this.getValueOrDashIfEmpty(value);
  });

  getValueOrDashIfEmpty = (value: string | number): string => {
    if (value) return "" + value;
    return "—";
  };

  removeSpaces(value: string): string {
    return value.replace(/\s/g, "");
  }

  formatMoney(amount: number): string {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "AUD",
    });
    return formatter.format(amount);
  }

  formatBooleanToUserFriendly(state: boolean): string {
    return state ? "Yes" : "No";
  }

  convertEnumToKeyValuePairs(en) {
    let arr = Object.keys(en).map((id) => {
      return {
        id,
        name: en[id as keyof typeof id],
      };
    });
    // Remove second set as its just the same things, but flipped
    arr = arr.slice(0, arr.length / 2);
    const arr1: KeyValuePairModel<number>[] = [];
    arr.forEach((item) => {
      arr1.push({
        id: parseInt(item.id),
        name: item.name,
      });
    });
    return arr1;
  }

  generateGUID(): string {
    const buffer = new Uint8Array(16);
    window.crypto.getRandomValues(buffer);
    buffer[6] = (buffer[6] & 0x0f) | 0x40;
    buffer[8] = (buffer[8] & 0x3f) | 0x80;
    const hex = Array.from(buffer, (byte) =>
      byte.toString(16).padStart(2, "0")
    );
    return `${hex[0]}${hex[1]}${hex[2]}${hex[3]}-${hex[4]}${hex[5]}-${hex[6]}${hex[7]}-${hex[8]}${hex[9]}-${hex[10]}${hex[11]}${hex[12]}${hex[13]}${hex[14]}${hex[15]}`;
  }

  isThisAValidAddress(address: IAddressModel): boolean {
    if (address === null) {
      return false;
    }

    const { city, state, postcode } = address;

    return (
      !!(city && city.trim()) &&
      !!(state && state.trim()) &&
      !!(postcode && postcode.trim())
    );
  }

  trackByFn(index: number, item: any): number {
    return item.id || index;
  }
}
